import { useState, useMemo, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { categories, getComicsByCategory, comics } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';

export default function Category() {
  const { slug = 'all' } = useParams();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const sortParam = searchParams.get('sort') || 'latest';
  const [activeCategory, setActiveCategory] = useState(slug);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 12;

  useEffect(() => {
    setActiveCategory(slug);
    setCurrentPage(1);
  }, [slug]);

  const filteredComics = useMemo(() => {
    let result = getComicsByCategory(activeCategory);

    // Search filter
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.title.toLowerCase().includes(q) ||
          c.author.toLowerCase().includes(q) ||
          c.tags.some((t) => t.includes(q))
      );
    }

    // Sort
    switch (sortParam) {
      case 'rating':
        result = [...result].sort((a, b) => b.rating - a.rating);
        break;
      case 'views':
        result = [...result].sort((a, b) => b.views - a.views);
        break;
      case 'latest':
      default:
        result = [...result].sort(
          (a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
        break;
    }

    return result;
  }, [activeCategory, searchQuery, sortParam]);

  const totalPages = Math.ceil(filteredComics.length / pageSize);
  const pagedComics = filteredComics.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const currentCategory = categories.find((c) => c.slug === activeCategory);

  const sortOptions = [
    { value: 'latest', label: '最新' },
    { value: 'rating', label: '评分' },
    { value: 'views', label: '人气' },
  ];

  return (
    <main className="pt-24 pb-16 bg-background-50 min-h-screen">
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-6" aria-label="面包屑导航">
          <Link to="/" className="hover:text-primary-500 transition-colors whitespace-nowrap">首页</Link>
          <span>/</span>
          <span className="text-foreground-800 font-medium">
            {searchQuery ? `搜索: "${searchQuery}"` : currentCategory?.name ? `${currentCategory.name}漫画` : '全部分类'}
          </span>
        </nav>

        {/* Page Title */}
        <div className="mb-8">
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-900">
            {searchQuery ? `搜索结果: "${searchQuery}"` : currentCategory?.name === '全部' || !currentCategory ? '全部漫画' : `${currentCategory.name}漫画`}
          </h1>
          <p className="text-sm text-foreground-500 mt-2">
            {searchQuery
              ? `找到 ${filteredComics.length} 部相关漫画`
              : `共 ${filteredComics.length} 部${currentCategory?.name ? currentCategory.name : ''}漫画作品`}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="flex items-center gap-1.5 overflow-x-auto pb-3 mb-6 border-b border-background-200 flex-wrap">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors cursor-pointer ${
                activeCategory === cat.slug
                  ? 'bg-primary-500 text-background-50'
                  : 'bg-background-100 text-foreground-600 hover:bg-background-200'
              }`}
            >
              {cat.name}
            </Link>
          ))}
        </div>

        {/* Sort Bar */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            {sortOptions.map((opt) => (
              <Link
                key={opt.value}
                to={`/category/${activeCategory}?sort=${opt.value}${searchQuery ? `&search=${encodeURIComponent(searchQuery)}` : ''}`}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  sortParam === opt.value
                    ? 'bg-secondary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {opt.label}
              </Link>
            ))}
          </div>
          <span className="text-xs text-foreground-500 whitespace-nowrap">
            第 {currentPage} / {totalPages} 页
          </span>
        </div>

        {/* Comic Grid */}
        {pagedComics.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5 mb-10">
            {pagedComics.map((comic) => (
              <ComicCard key={comic.id} comic={comic} size="medium" />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 text-foreground-400">
              <i className="ri-search-line text-4xl"></i>
            </div>
            <p className="text-foreground-500">没有找到相关漫画</p>
            <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 mt-2 inline-block whitespace-nowrap">
              查看全部漫画
            </Link>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-s-line"></i>
            </button>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
              <button
                key={page}
                onClick={() => setCurrentPage(page)}
                className={`w-9 h-9 flex items-center justify-center rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                  currentPage === page
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                {page}
              </button>
            ))}
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="w-9 h-9 flex items-center justify-center rounded-lg bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <i className="ri-arrow-right-s-line"></i>
            </button>
          </div>
        )}
      </div>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "name": `${currentCategory?.name || '全部'}漫画 - 免费漫画网`,
        "description": `免费漫画网${currentCategory?.name || ''}漫画分类，正版韩漫大全，免费韩漫下拉式阅读。`,
        "url": `https://www.freemanhua.com/category/${activeCategory}`,
        "isPartOf": {
          "@type": "WebSite",
          "name": "免费漫画网",
          "url": "https://www.freemanhua.com/"
        }
      }) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "首页", "item": "https://www.freemanhua.com/" },
          { "@type": "ListItem", "position": 2, "name": `${currentCategory?.name || '全部'}漫画`, "item": `https://www.freemanhua.com/category/${activeCategory}` },
        ],
      }) }} />
    </main>
  );
}